import { computed, type Signal } from '@angular/core';
import { readVaultItemsPerPageFromPreferences } from '@nebula/shared-vault/data-access';
import {
  patchState,
  signalStore,
  type WritableStateSource,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  clearPersistedState,
  loadPersistedState,
  savePersistedState,
} from './signal-store-persist.util';
import type {
  IngestProjectRow,
  IngestProjectsTableState,
} from 'libs/vault/src/lib/domain/src/lib/ingest/ingest-store.model';

type IngestProjectsTableStateSignals = {
  searchTerm: Signal<string>;
  sortColumn: Signal<keyof IngestProjectRow | null>;
  sortDirection: Signal<'asc' | 'desc' | null>;
  pageSize: Signal<number>;
  pageIndex: Signal<number>;
  loading: Signal<boolean>;
  projects: Signal<IngestProjectRow[]>;
};

const STORAGE_KEY = 'ingest_projects_table_state';
const STORAGE_VERSION = 1;

type IngestProjectsTablePersistedState = Pick<
  IngestProjectsTableState,
  'searchTerm' | 'sortColumn' | 'sortDirection' | 'pageSize' | 'pageIndex'
>;

const persistConfig = {
  key: STORAGE_KEY,
  version: STORAGE_VERSION,
  storage: 'session' as const,
};

const initialState: IngestProjectsTableState = {
  searchTerm: '',
  sortColumn: 'name',
  sortDirection: 'asc',
  pageSize: readVaultItemsPerPageFromPreferences(),
  pageIndex: 0,
  loading: false,
  projects: [],
};

function isIngestProjectsPersistedState(
  value: unknown,
): value is IngestProjectsTablePersistedState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  const hasValidSortDirection =
    v['sortDirection'] === 'asc' ||
    v['sortDirection'] === 'desc' ||
    v['sortDirection'] === null;
  const hasValidSortColumn =
    typeof v['sortColumn'] === 'string' || v['sortColumn'] === null;
  return (
    typeof v['searchTerm'] === 'string' &&
    hasValidSortDirection &&
    hasValidSortColumn &&
    typeof v['pageSize'] === 'number' &&
    Number.isFinite(v['pageSize']) &&
    typeof v['pageIndex'] === 'number' &&
    Number.isFinite(v['pageIndex'])
  );
}

function loadSavedState(): Partial<IngestProjectsTablePersistedState> {
  const persisted = loadPersistedState(
    persistConfig,
    isIngestProjectsPersistedState,
  );
  if (!persisted) return {};
  const pageIndex = Number(persisted.pageIndex);
  return {
    searchTerm: persisted.searchTerm ?? initialState.searchTerm,
    sortColumn: persisted.sortColumn ?? initialState.sortColumn,
    sortDirection: persisted.sortDirection ?? initialState.sortDirection,
    pageSize: Number(persisted.pageSize) || initialState.pageSize,
    pageIndex:
      Number.isFinite(pageIndex) && pageIndex >= 0
        ? Math.floor(pageIndex)
        : initialState.pageIndex,
  };
}

const persistedInitialState: IngestProjectsTableState = {
  ...initialState,
  ...loadSavedState(),
};

type IngestProjectsTableStoreLike = WritableStateSource<IngestProjectsTableState> & {
  searchTerm: () => string;
  sortColumn: () => keyof IngestProjectRow | null;
  sortDirection: () => 'asc' | 'desc' | null;
  pageSize: () => number;
  pageIndex: () => number;
  projects: () => IngestProjectRow[];
};

function clampPageIndexForRowCount(
  pageIndex: number,
  rowCount: number,
  pageSize: number,
): number {
  if (rowCount <= 0) return 0;
  const ps = Math.max(1, pageSize);
  const maxIdx = Math.max(0, Math.ceil(rowCount / ps) - 1);
  return Math.min(Math.max(0, pageIndex), maxIdx);
}

export const ingestProjectsTableStore = signalStore(
  { providedIn: 'root' },
  withState(persistedInitialState),
  withComputed((state) => {
    const s = state as unknown as IngestProjectsTableStateSignals;
    return {
      hasData: computed(() => s.projects().length > 0),
      isEmpty: computed(
        () => !s.loading() && s.projects().length === 0,
      ),
    };
  }),
  withMethods((store) => createIngestProjectsTableMethods(store)),
);

function createIngestProjectsTableMethods(store: IngestProjectsTableStoreLike) {
  return {
    loadProjects(rows: IngestProjectRow[]): void {
      const pageSize = store.pageSize();
      const nextIndex = clampPageIndexForRowCount(
        store.pageIndex(),
        rows.length,
        pageSize,
      );
      patchState(store, { projects: rows, loading: false, pageIndex: nextIndex });
      saveToStorage(store);
    },
    startLoading(): void {
      patchState(store, { loading: true });
    },
    setLoading(loading: boolean): void {
      patchState(store, { loading });
    },
    setSearchTerm(term: string): void {
      const next = term ?? '';
      if (store.searchTerm() === next) return;
      patchState(store, { searchTerm: next, pageIndex: 0 });
      saveToStorage(store);
    },
    setSort(
      column: keyof IngestProjectRow | null,
      direction: 'asc' | 'desc' | null,
    ): void {
      if (store.sortColumn() === column && store.sortDirection() === direction) return;
      patchState(store, { sortColumn: column, sortDirection: direction, pageIndex: 0 });
      saveToStorage(store);
    },
    setPageSize(size: number): void {
      const nextSize = Math.max(1, size);
      if (store.pageSize() === nextSize) return;
      const nextIndex = clampPageIndexForRowCount(
        store.pageIndex(),
        store.projects().length,
        nextSize,
      );
      patchState(store, { pageSize: nextSize, pageIndex: nextIndex });
      saveToStorage(store);
    },
    setPageIndex(index: number): void {
      const safe = Math.max(0, Number.isFinite(index) ? Math.floor(index) : 0);
      if (store.pageIndex() === safe) return;
      patchState(store, { pageIndex: safe });
      saveToStorage(store);
    },
    clearFilters(): void {
      patchState(store, {
        searchTerm: '',
        sortColumn: 'name',
        sortDirection: 'asc',
        pageIndex: 0,
      });
      saveToStorage(store);
    },
    resetState(): void {
      patchState(store, initialState);
      clearPersistedState(persistConfig);
    },
  };
}

function saveToStorage(store: {
  searchTerm: () => string;
  sortColumn: () => keyof IngestProjectRow | null;
  sortDirection: () => 'asc' | 'desc' | null;
  pageSize: () => number;
  pageIndex: () => number;
}): void {
  const stateToSave: IngestProjectsTablePersistedState = {
    searchTerm: store.searchTerm(),
    sortColumn: store.sortColumn(),
    sortDirection: store.sortDirection(),
    pageSize: store.pageSize(),
    pageIndex: store.pageIndex(),
  };
  savePersistedState(persistConfig, stateToSave);
}

export type IngestProjectsTableStore = typeof ingestProjectsTableStore;
